// Bilingual content (EN / ES) for Marine Nav Electronics
export const IMAGES = {
  heroSuperyachtNight: "https://images.unsplash.com/photo-1655286029136-95a47d455999?crop=entropy&cs=srgb&fm=jpg&q=95&w=3200",
  yachtHelm: "https://images.unsplash.com/photo-1601919972568-768b9bbcf548?crop=entropy&cs=srgb&fm=jpg&q=95&w=2400",
  radarMast: "https://images.unsplash.com/photo-1782940983972-357de3c6735a?crop=entropy&cs=srgb&fm=jpg&q=95&w=2400",
  superyacht: "https://images.unsplash.com/photo-1598737285721-29346a5c9278?crop=entropy&cs=srgb&fm=jpg&q=95&w=2800",
  sportfish: "https://images.pexels.com/photos/22912091/pexels-photo-22912091.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=2800",
  centerConsole: "https://images.unsplash.com/photo-1674419404519-54fa8a774aa2?crop=entropy&cs=srgb&fm=jpg&q=95&w=2800",
  tender: "https://images.pexels.com/photos/30939177/pexels-photo-30939177.jpeg?auto=compress&cs=srgb&w=2800",
  runabout: "https://images.unsplash.com/photo-1656625175362-31ecc2803a9e?crop=entropy&cs=srgb&fm=jpg&q=95&w=2800",
  marinaAerial: "https://images.pexels.com/photos/11061670/pexels-photo-11061670.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=2800",
  technician: "https://images.pexels.com/photos/3823425/pexels-photo-3823425.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=2400",
  nauticalChart: "https://images.pexels.com/photos/3098859/pexels-photo-3098859.jpeg?auto=compress&cs=srgb&w=2000",
  bridge: "https://images.pexels.com/photos/3582491/pexels-photo-3582491.jpeg?auto=compress&cs=srgb&w=2400",
  floridaMap: "https://commons.wikimedia.org/wiki/Special:FilePath/South_Florida_Satellite_Image_Map.jpg?width=1600",
};

// Hotspots anchored on the hero superyacht photo (x/y = % of hero container, desktop crop)
export const HOTSPOTS = [
  { id: "radar", x: 50, y: 22, side: "right", en: { t: "Solid-State Radar", d: "Doppler target separation and dual-range scanning on the mast." }, es: { t: "Radar de Estado Sólido", d: "Separación Doppler y escaneo de doble rango en el mástil." } },
  { id: "starlink", x: 58, y: 33, side: "right", en: { t: "Starlink Maritime", d: "High-throughput connectivity, offshore to the dock." }, es: { t: "Starlink Marítimo", d: "Conectividad de alto rendimiento, mar adentro y en muelle." } },
  { id: "gps", x: 42, y: 39, side: "left", en: { t: "GPS / Chartplotter", d: "TimeZero-ready helm with photographic charts." }, es: { t: "GPS / Chartplotter", d: "Timón listo para TimeZero con cartas fotográficas." } },
  { id: "vhf", x: 62, y: 45, side: "right", en: { t: "VHF & AIS", d: "SOLAS-grade comms and Class-B AIS integration." }, es: { t: "VHF y AIS", d: "Comunicaciones SOLAS e integración AIS Clase-B." } },
  { id: "nmea", x: 49, y: 50, side: "left", en: { t: "NMEA 2000 Backbone", d: "Certified network spine wiring every sensor on board." }, es: { t: "Backbone NMEA 2000", d: "Red certificada que cablea cada sensor a bordo." } },
];

export const CONTENT = {
  en: {
    nav: { services: "Services", tech: "TimeZero", boats: "Vessels", area: "Service Area", work: "Brands", contact: "Get a Quote" },
    hero: {
      overline: "Marine Electronics · Fort Lauderdale · Miami · Palm Beach",
      titleLines: ["Your bridge,", "wired right."],
      sub: "Certified installation, maintenance and refit of navigation, communication and safety systems — from tenders to the South Florida luxury fleet.",
      cta: "Request a Survey",
      cta2: "Explore Systems",
      hint: "Hover the instruments",
      stat1: "10+ yrs", stat1l: "Bridge electronics",
      stat2: "NMEA 2000", stat2l: "Certified specialists",
      stat3: "EN · ES", stat3l: "Two languages",
    },
    services: {
      overline: "01 — Capabilities",
      title: "Every device on your network, handled.",
      sub: "From the chartplotter at the helm to the antenna on the mast — installed with leading brands, wired to spec, and tested before handover.",
      items: [
        { t: "Navigation Systems", d: "Bridge refits, radar, chartplotters and GPS — full helm installations." },
        { t: "Communications", d: "VHF and comm systems built to SOLAS standards for every passage." },
        { t: "Gyro Compass", d: "Maintenance, calibration and heading-accuracy checks to spec." },
        { t: "Sensors & Instruments", d: "Wind sensors, transducers and instruments networked over NMEA 2000." },
        { t: "Starlink Maritime", d: "High-speed satellite internet installed and dialed in offshore." },
        { t: "Field Services", d: "On-site installs, CCTV, sea-trial support and diagnostics — fast response." },
      ],
    },
    tech: {
      overline: "02 — TimeZero Installers",
      title: "See the Bahamas before your bow does.",
      sub: "We install TimeZero — the chart system captains trust for the islands. Photographic charts, precise depths and safe routes from Bimini to the Exumas.",
      cta: "Get TimeZero on your helm",
      widgets: { speed: "SOG", heading: "HDG", depth: "DPT", bus: "N2K BUS" },
    },
    boats: {
      overline: "03 — Every Vessel",
      title: "If it floats and carries electronics, it's welcome aboard.",
      sub: "The same certified work and the same care — whether a 160-ft superyacht, the tender that follows it, or the weekend boat you take out with the family.",
      types: [
        { id: "superyacht", t: "Superyacht", img: "superyacht", d: "Full bridge integration, redundant navigation, comms and entertainment." },
        { id: "sportfish", t: "Sportfish", img: "sportfish", d: "Tournament-grade sonar, radar, outrigger comms and satellite." },
        { id: "console", t: "Center Console", img: "centerConsole", d: "Rugged, waterproof helm electronics for the run offshore." },
        { id: "runabout", t: "Runabout", img: "runabout", d: "Clean instrument packages that respect classic lines." },
        { id: "tender", t: "Tender", img: "tender", d: "Compact GPS, VHF and AIS that punch above their size." },
      ],
    },
    area: {
      overline: "04 — Service Area",
      title: "South Florida, covered.",
      sub: "Mobile service across the Gold Coast marinas and yards. Home port 26.1224° N · 80.1373° W.",
      allUS: "Based in South Florida — and available for installs, refits and service across the United States.",
      marinas: ["Pier Sixty-Six", "Bahia Mar", "Miami Beach Marina", "Old Port Cove", "Las Olas", "Rybovich"],
      cities: [{ n: "Palm Beach", tag: "Palm Beach County" }, { n: "Fort Lauderdale", tag: "Home port" }, { n: "Miami", tag: "Miami-Dade" }],
    },
    process: {
      overline: "05 — How we work",
      title: "From first contact to sea trial.",
      steps: [
        { n: "01", t: "Contact & diagnosis", d: "Tell me what your vessel needs — a new install, a refit, or a fault to solve." },
        { n: "02", t: "Survey & quote", d: "I inspect the systems on board and give a clear, itemized quote before any work." },
        { n: "03", t: "Install or repair", d: "Work at your marina or dock, with leading-brand equipment and clean, labeled wiring." },
        { n: "04", t: "Test & handover", d: "Every system verified before handover — and I stay available for support afterward." },
      ],
    },
    config: {
      overline: "06 — System Configurator",
      title: "Find your package in 3 steps.",
      steps: ["Vessel", "Priorities", "Contact"],
      q1: "What do you run?", q2: "What matters most?", q3: "Where do we send it?",
      priorities: ["Navigation", "Radar", "Connectivity", "Communication", "Entertainment", "Autopilot"],
      recommend: "Recommended systems",
      recommendNote: "Current-generation gear we install and support for this vessel.",
      submit: "Send Survey Request",
      next: "Next", back: "Back",
    },
    work: {
      overline: "07 — Brands We Install",
      title: "The names on your helm.",
      sub: "We're independent — so we specify and install the best hardware for your vessel, not whatever one label sells. Factory-current on the brands captains trust.",
      items: [
        { n: "Furuno", d: "Radar, NavNet TZtouch3 MFDs, sounders & autopilots." },
        { n: "Garmin", d: "GPSMAP chartplotters, sonar, radar & smart instruments." },
        { n: "Simrad", d: "Helm displays, broadband radar & autopilot systems." },
        { n: "Raymarine", d: "Axiom MFDs, thermal cameras & DSC VHF." },
        { n: "Airmar", d: "Smart transducers & weather sensors over NMEA 2000." },
        { n: "TimeZero", d: "Professional charting, PBG bathymetry & radar overlay." },
        { n: "Fusion", d: "Marinized audio, zoned streaming and helm control." },
        { n: "Starlink", d: "Maritime high-speed satellite internet, dialed in." },
      ],
    },
    contact: {
      overline: "08 — Get a Quote",
      title: "Let's talk about your boat.",
      name: "Full name", email: "Email", phone: "Phone", vessel: "Vessel & length", message: "What do you need?",
      submit: "Send Request",
      success: "Request received. We'll be in touch shortly.",
      email_label: "Email", area_label: "Service area", hours_label: "Hours",
      hours: "Mon–Sun · 8 AM – 5 PM",
    },
    footer: { tag: "Certified marine electronics · South Florida", rights: "All rights reserved.", independent: "Brand names are trademarks of their respective manufacturers. Marine Nav Electronics is an independent service company." },
    loader: "Acquiring systems",
    form: { required: "Required", invalidEmail: "Enter a valid email" },
  },
  es: {
    nav: { services: "Servicios", tech: "TimeZero", boats: "Embarcaciones", area: "Área de Servicio", work: "Marcas", contact: "Cotizar" },
    hero: {
      overline: "Electrónica Marina · Fort Lauderdale · Miami · Palm Beach",
      titleLines: ["Tu puente,", "bien cableado."],
      sub: "Instalación, mantenimiento y refit certificado de sistemas de navegación, comunicación y seguridad — desde tenders hasta la flota de lujo de South Florida.",
      cta: "Solicitar Survey",
      cta2: "Explorar Sistemas",
      hint: "Pasa sobre los instrumentos",
      stat1: "10+ años", stat1l: "Electrónica de puente",
      stat2: "NMEA 2000", stat2l: "Especialistas certificados",
      stat3: "EN · ES", stat3l: "Dos idiomas",
    },
    services: {
      overline: "01 — Capacidades",
      title: "Cada equipo de tu red, resuelto.",
      sub: "Del chartplotter en el timón a la antena en el mástil — instalado con marcas líderes, cableado a especificación y probado antes de la entrega.",
      items: [
        { t: "Sistemas de Navegación", d: "Refit de puente, radar, chartplotters y GPS — instalaciones completas de timón." },
        { t: "Comunicaciones", d: "VHF y sistemas de comunicación bajo estándar SOLAS para cada travesía." },
        { t: "Girocompás", d: "Mantenimiento, calibración y verificación de rumbo a especificación." },
        { t: "Sensores e Instrumentos", d: "Sensores de viento, transductores e instrumentos en red NMEA 2000." },
        { t: "Starlink Marítimo", d: "Internet satelital de alta velocidad instalado y ajustado en altamar." },
        { t: "Servicios de Campo", d: "Instalaciones, CCTV, soporte en pruebas de mar y diagnóstico — respuesta rápida." },
      ],
    },
    tech: {
      overline: "02 — Instaladores TimeZero",
      title: "Ve las Bahamas antes que tu proa.",
      sub: "Instalamos TimeZero — el sistema de cartas en que confían los capitanes para las islas. Cartas fotográficas, profundidades precisas y rutas seguras de Bimini a los Exumas.",
      cta: "TimeZero en tu timón",
      widgets: { speed: "SOG", heading: "RMB", depth: "PRF", bus: "BUS N2K" },
    },
    boats: {
      overline: "03 — Cada Embarcación",
      title: "Si flota y lleva electrónica, es bienvenida a bordo.",
      sub: "El mismo trabajo certificado y el mismo cuidado — un superyate de 160 pies, el tender que lo sigue, o el bote de fin de semana con la familia.",
      types: [
        { id: "superyacht", t: "Superyate", img: "superyacht", d: "Integración total de puente, navegación, comunicaciones y entretenimiento redundantes." },
        { id: "sportfish", t: "Sportfish", img: "sportfish", d: "Sonar de torneo, radar, comunicaciones en outriggers y satélite." },
        { id: "console", t: "Center Console", img: "centerConsole", d: "Electrónica de timón robusta y estanca para altamar." },
        { id: "runabout", t: "Runabout", img: "runabout", d: "Paquetes de instrumentos limpios que respetan las líneas clásicas." },
        { id: "tender", t: "Tender", img: "tender", d: "GPS, VHF y AIS compactos de gran rendimiento." },
      ],
    },
    area: {
      overline: "04 — Área de Servicio",
      title: "South Florida, cubierto.",
      sub: "Servicio móvil en las marinas y astilleros de la Gold Coast. Puerto base 26.1224° N · 80.1373° W.",
      allUS: "Con base en South Florida — y disponibles para instalaciones, refits y servicio en todo Estados Unidos.",
      marinas: ["Pier Sixty-Six", "Bahia Mar", "Miami Beach Marina", "Old Port Cove", "Las Olas", "Rybovich"],
      cities: [{ n: "Palm Beach", tag: "Palm Beach County" }, { n: "Fort Lauderdale", tag: "Puerto base" }, { n: "Miami", tag: "Miami-Dade" }],
    },
    process: {
      overline: "05 — Cómo trabajamos",
      title: "Del primer contacto a la prueba de mar.",
      steps: [
        { n: "01", t: "Contacto y diagnóstico", d: "Cuéntame qué necesita tu embarcación — instalación nueva, refit o una falla." },
        { n: "02", t: "Survey y cotización", d: "Inspecciono los sistemas a bordo y doy una cotización clara antes de empezar." },
        { n: "03", t: "Instalación o reparación", d: "Trabajo en tu marina o muelle, con equipos de marca y cableado limpio y etiquetado." },
        { n: "04", t: "Prueba y entrega", d: "Cada sistema verificado antes de la entrega — y sigo disponible para soporte." },
      ],
    },
    config: {
      overline: "06 — Configurador",
      title: "Encuentra tu paquete en 3 pasos.",
      steps: ["Embarcación", "Prioridades", "Contacto"],
      q1: "¿Qué navegas?", q2: "¿Qué es lo más importante?", q3: "¿A dónde lo enviamos?",
      priorities: ["Navegación", "Radar", "Conectividad", "Comunicación", "Entretenimiento", "Piloto Auto."],
      recommend: "Sistemas recomendados",
      recommendNote: "Equipos de última generación que instalamos y soportamos para esta embarcación.",
      submit: "Enviar Solicitud",
      next: "Siguiente", back: "Atrás",
    },
    work: {
      overline: "07 — Marcas Que Instalamos",
      title: "Los nombres en tu timón.",
      sub: "Somos independientes — especificamos e instalamos el mejor hardware para tu embarcación, no lo que vende una sola marca. Al día con las marcas en que confían los capitanes.",
      items: [
        { n: "Furuno", d: "Radar, MFD NavNet TZtouch3, sondas y pilotos automáticos." },
        { n: "Garmin", d: "Chartplotters GPSMAP, sonar, radar e instrumentos smart." },
        { n: "Simrad", d: "Displays de timón, radar broadband y pilotos automáticos." },
        { n: "Raymarine", d: "MFD Axiom, cámaras térmicas y VHF DSC." },
        { n: "Airmar", d: "Transductores smart y sensores meteo en NMEA 2000." },
        { n: "TimeZero", d: "Cartografía profesional, batimetría PBG y overlay de radar." },
        { n: "Fusion", d: "Audio marinizado, streaming por zonas y control desde el timón." },
        { n: "Starlink", d: "Internet satelital marítimo de alta velocidad, ajustado." },
      ],
    },
    contact: {
      overline: "08 — Cotización",
      title: "Hablemos de tu barco.",
      name: "Nombre completo", email: "Correo", phone: "Teléfono", vessel: "Embarcación y eslora", message: "¿Qué necesitas?",
      submit: "Enviar Solicitud",
      success: "Solicitud recibida. Te contactaremos pronto.",
      email_label: "Correo", area_label: "Área de servicio", hours_label: "Horario",
      hours: "Lun–Dom · 8 AM – 5 PM",
    },
    footer: { tag: "Electrónica marina certificada · South Florida", rights: "Todos los derechos reservados.", independent: "Las marcas son propiedad de sus respectivos fabricantes. Marine Nav Electronics es una empresa de servicio independiente." },
    loader: "Adquiriendo sistemas",
    form: { required: "Requerido", invalidEmail: "Ingresa un correo válido" },
  },
};

export const BRANDS = ["FURUNO", "GARMIN", "SIMRAD", "RAYMARINE", "AIRMAR", "TIMEZERO", "STARLINK"];
